(function() {
  var DEBUG_FLAG = '__SHOPIFY_OSANO_CONSENT_DEBUG__';
  var LOG_PREFIX = '[ShopifyOsanoConsentSync]';
  var WAIT_TIMEOUT_MS = 15000;
  var WAIT_INTERVAL_MS = 100;
  var CONSENT_EVENTS = [
    'osano-cm-initialized',
    'osano-cm-consent-saved'
  ];
  var ANALYTICS_ALIASES = ['ANALYTICS', 'analytics'];
  var MARKETING_ALIASES = ['MARKETING', 'marketing', 'ADVERTISING', 'ads'];
  var PREFERENCES_ALIASES = [
    'PERSONALIZATION',
    'personalization',
    'PREFERENCES',
    'preferences',
    'FUNCTIONAL',
    'functional'
  ];
  var SALE_OF_DATA_ALIASES = [
    'SALE_OF_DATA',
    'SALEOFDATA',
    'DATA_SALE',
    'DATA_SHARE',
    'SALE_OR_SHARE_OF_DATA',
    'SALE_SHARE_OF_DATA'
  ];
  var OPT_OUT_ALIASES = ['OPT_OUT', 'OPTOUT', 'DO_NOT_SELL', 'DO_NOT_SELL_OR_SHARE'];
  var hasStarted = false;
  var shopifyConsentApiPromise = null;
  var lastSyncedConsentJson = null;
  var pendingConsentJson = null;

  function isDebugEnabled() {
    return Boolean(window[DEBUG_FLAG]);
  }

  function debugLog() {
    if (!isDebugEnabled() || !window.console || !window.console.log) return;

    var args = Array.prototype.slice.call(arguments);
    args.unshift(LOG_PREFIX);
    window.console.log.apply(window.console, args);
  }

  function debugWarn() {
    if (!isDebugEnabled() || !window.console || !window.console.warn) return;

    var args = Array.prototype.slice.call(arguments);
    args.unshift(LOG_PREFIX);
    window.console.warn.apply(window.console, args);
  }

  function waitFor(testFn, label, timeoutMs) {
    var startedAt = Date.now();

    return new Promise(function(resolve, reject) {
      function check() {
        var value;

        try {
          value = testFn();
        } catch (error) {
          reject(error);
          return;
        }

        if (value) {
          resolve(value);
          return;
        }

        if (Date.now() - startedAt >= timeoutMs) {
          reject(new Error(label + ' was not available after ' + timeoutMs + 'ms'));
          return;
        }

        window.setTimeout(check, WAIT_INTERVAL_MS);
      }

      check();
    });
  }

  function waitForShopifyLoadFeatures() {
    return waitFor(function() {
      return window.Shopify && typeof window.Shopify.loadFeatures === 'function'
        ? window.Shopify.loadFeatures
        : null;
    }, 'window.Shopify.loadFeatures', WAIT_TIMEOUT_MS);
  }

  function waitForOsanoCm() {
    return waitFor(function() {
      return window.Osano && window.Osano.cm ? window.Osano.cm : null;
    }, 'window.Osano.cm', WAIT_TIMEOUT_MS);
  }

  function loadShopifyConsentApi() {
    if (shopifyConsentApiPromise) return shopifyConsentApiPromise;

    shopifyConsentApiPromise = waitForShopifyLoadFeatures()
      .then(function(loadFeatures) {
        return new Promise(function(resolve, reject) {
          loadFeatures(
            [
              {
                name: 'consent-tracking-api',
                version: '0.1'
              }
            ],
            function(error) {
              if (error) {
                reject(error);
                return;
              }

              if (!window.Shopify || !window.Shopify.customerPrivacy) {
                reject(new Error('window.Shopify.customerPrivacy was not available after consent API load'));
                return;
              }

              resolve(window.Shopify.customerPrivacy);
            }
          );
        });
      });
    shopifyConsentApiPromise.catch(function() {
      shopifyConsentApiPromise = null;
    });

    return shopifyConsentApiPromise;
  }

  function getOsanoConsent() {
    try {
      if (window.Osano && window.Osano.cm && typeof window.Osano.cm.getConsent === 'function') {
        return window.Osano.cm.getConsent();
      }
    } catch (error) {
      debugWarn('Unable to read Osano consent', error);
    }

    return null;
  }

  function normalizeKey(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  function normalizeConsentValue(value) {
    if (value === true || value === 1) return true;
    if (value === false || value === 0 || value === null) return false;

    if (typeof value === 'string') {
      var normalized = value.toLowerCase().replace(/[\s_-]/g, '');

      if (
        normalized === 'accept' ||
        normalized === 'accepted' ||
        normalized === 'allow' ||
        normalized === 'allowed' ||
        normalized === 'grant' ||
        normalized === 'granted' ||
        normalized === 'optin' ||
        normalized === 'yes' ||
        normalized === 'y' ||
        normalized === 'on' ||
        normalized === 'true' ||
        normalized === '1'
      ) {
        return true;
      }

      if (
        normalized === 'deny' ||
        normalized === 'denied' ||
        normalized === 'disallow' ||
        normalized === 'disallowed' ||
        normalized === 'decline' ||
        normalized === 'declined' ||
        normalized === 'reject' ||
        normalized === 'rejected' ||
        normalized === 'optout' ||
        normalized === 'no' ||
        normalized === 'n' ||
        normalized === 'off' ||
        normalized === 'false' ||
        normalized === '0'
      ) {
        return false;
      }
    }

    return null;
  }

  function getObjectValueByAliases(objectValue, aliases) {
    var normalizedAliases = aliases.map(normalizeKey);
    var keys;
    var i;
    var key;

    if (!objectValue || typeof objectValue !== 'object') return undefined;

    keys = Object.keys(objectValue);

    for (i = 0; i < keys.length; i += 1) {
      key = keys[i];

      if (normalizedAliases.indexOf(normalizeKey(key)) !== -1) {
        return objectValue[key];
      }
    }

    return undefined;
  }

  function arrayIncludesAlias(arrayValue, aliases) {
    var normalizedAliases = aliases.map(normalizeKey);
    var i;

    if (!Array.isArray(arrayValue)) return false;

    for (i = 0; i < arrayValue.length; i += 1) {
      if (normalizedAliases.indexOf(normalizeKey(arrayValue[i])) !== -1) {
        return true;
      }
    }

    return false;
  }

  function readConsentDecision(value, aliases) {
    var directDecision = normalizeConsentValue(value);
    var nestedDecision;
    var statusKeys = [
      'accepted',
      'allowed',
      'enabled',
      'selected',
      'value',
      'status',
      'state',
      'consent'
    ];
    var i;

    if (directDecision !== null) return directDecision;

    if (Array.isArray(value)) {
      return arrayIncludesAlias(value, aliases) ? true : null;
    }

    if (!value || typeof value !== 'object') return null;

    for (i = 0; i < statusKeys.length; i += 1) {
      nestedDecision = normalizeConsentValue(value[statusKeys[i]]);

      if (nestedDecision !== null) return nestedDecision;
    }

    return null;
  }

  function findConsentDecision(consent, aliases) {
    var containerKeys = [
      'categories',
      'category',
      'consents',
      'consent',
      'preferences',
      'purposes',
      'enabled',
      'accepted',
      'acceptedCategories',
      'allowedCategories'
    ];
    var directValue;
    var decision;
    var container;
    var i;

    if (!consent || typeof consent !== 'object') return null;

    directValue = getObjectValueByAliases(consent, aliases);
    decision = readConsentDecision(directValue, aliases);

    if (decision !== null) return decision;

    for (i = 0; i < containerKeys.length; i += 1) {
      container = consent[containerKeys[i]];

      if (Array.isArray(container) && arrayIncludesAlias(container, aliases)) {
        return true;
      }

      if (container && typeof container === 'object') {
        directValue = getObjectValueByAliases(container, aliases);
        decision = readConsentDecision(directValue, aliases);

        if (decision !== null) return decision;
      }
    }

    return null;
  }

  function isAcceptedCategory(consent, aliases) {
    return findConsentDecision(consent, aliases) === true;
  }

  function isSaleOfDataAllowed(consent) {
    var optOutDecision = findConsentDecision(consent, OPT_OUT_ALIASES);
    var explicitSaleOfDataDecision = findConsentDecision(consent, SALE_OF_DATA_ALIASES);
    var marketingDecision = findConsentDecision(consent, MARKETING_ALIASES);

    /*
      Osano exposes "Do Not Sell or Share My Personal Information" as OPT_OUT.
      Its polarity is inverted from Shopify sale_of_data:
      OPT_OUT: ACCEPT means the visitor opted out, so sale_of_data must be false.
    */
    if (optOutDecision === true) {
      return false;
    }

    if (explicitSaleOfDataDecision !== null) {
      return explicitSaleOfDataDecision === true;
    }

    /*
      If Osano has no positive sale/share category, use Marketing consent as
      the allow signal for Shopify sale_of_data.
    */
    return marketingDecision === true;
  }

  function hasRecognizedConsentDecision(consent) {
    return (
      findConsentDecision(consent, ANALYTICS_ALIASES) !== null ||
      findConsentDecision(consent, MARKETING_ALIASES) !== null ||
      findConsentDecision(consent, PREFERENCES_ALIASES) !== null ||
      findConsentDecision(consent, OPT_OUT_ALIASES) !== null ||
      findConsentDecision(consent, SALE_OF_DATA_ALIASES) !== null
    );
  }

  function mapOsanoConsentToShopify(consent) {
    /*
      Assumptions based on the currently observed theme code and Osano CMP API patterns:
      - Osano category keys can be uppercase strings such as ANALYTICS and MARKETING.
      - Category values can be booleans or status strings such as ACCEPT / DENY.
      - Some Osano configurations may nest categories under objects such as categories,
        consent, preferences, or purposes.

      Once the real production payload is inspected with:
        window.__SHOPIFY_OSANO_CONSENT_DEBUG__ = true
        window.syncOsanoConsentToShopifyDebug()

      tighten the alias lists below to the exact category names configured in Osano.
    */
    return {
      analytics: isAcceptedCategory(consent, ANALYTICS_ALIASES),
      marketing: isAcceptedCategory(consent, MARKETING_ALIASES),
      preferences: isAcceptedCategory(consent, PREFERENCES_ALIASES),
      sale_of_data: isSaleOfDataAllowed(consent)
    };
  }

  function serializeShopifyConsent(consent) {
    return JSON.stringify({
      analytics: Boolean(consent.analytics),
      marketing: Boolean(consent.marketing),
      preferences: Boolean(consent.preferences),
      sale_of_data: Boolean(consent.sale_of_data)
    });
  }

  function syncConsentToShopify(rawConsent, source) {
    return loadShopifyConsentApi()
      .then(function(customerPrivacy) {
        var mappedConsent = mapOsanoConsentToShopify(rawConsent);
        var mappedConsentJson = serializeShopifyConsent(mappedConsent);

        if (!hasRecognizedConsentDecision(rawConsent)) {
          debugLog('Skipping consent sync because the Osano payload did not include recognized category decisions', {
            source: source,
            rawOsanoConsent: rawConsent
          });
          return null;
        }

        if (mappedConsentJson === lastSyncedConsentJson || mappedConsentJson === pendingConsentJson) {
          debugLog('Skipping unchanged consent from ' + source, mappedConsent);
          return mappedConsent;
        }

        pendingConsentJson = mappedConsentJson;
        debugLog('Syncing consent from ' + source, {
          rawOsanoConsent: rawConsent,
          mappedShopifyConsent: mappedConsent
        });

        return new Promise(function(resolve) {
          customerPrivacy.setTrackingConsent(mappedConsent, function(error) {
            pendingConsentJson = null;

            if (error) {
              debugWarn('Shopify setTrackingConsent failed', error);
              resolve(mappedConsent);
              return;
            }

            lastSyncedConsentJson = mappedConsentJson;
            debugLog('Shopify consent synced', mappedConsent);
            resolve(mappedConsent);
          });
        });
      })
      .catch(function(error) {
        debugWarn('Consent sync skipped', error);
        return null;
      });
  }

  function syncCurrentOsanoConsent(source) {
    var consent = getOsanoConsent();

    if (!consent) {
      debugLog('No Osano consent object available for ' + source);
      return Promise.resolve(null);
    }

    return syncConsentToShopify(consent, source);
  }

  function addOsanoEventListener(cm, eventName, handler) {
    if (cm && typeof cm.addEventListener === 'function') {
      cm.addEventListener(eventName, handler);
      return true;
    }

    return false;
  }

  function addDocumentEventListener(eventName, handler) {
    if (document && typeof document.addEventListener === 'function') {
      document.addEventListener(eventName, handler);
      return true;
    }

    return false;
  }

  function getEventConsent(eventOrConsent) {
    if (!eventOrConsent) return getOsanoConsent();

    if (eventOrConsent.detail) {
      return eventOrConsent.detail.consent || eventOrConsent.detail;
    }

    return eventOrConsent;
  }

  function registerOsanoListeners(cm) {
    var i;

    for (i = 0; i < CONSENT_EVENTS.length; i += 1) {
      (function(eventName) {
        var handler = function(eventOrConsent) {
          var rawConsent = getEventConsent(eventOrConsent);

          if (!rawConsent) {
            debugLog('Osano event did not include consent; reading current consent', eventName);
            syncCurrentOsanoConsent(eventName);
            return;
          }

          syncConsentToShopify(rawConsent, eventName);
        };

        if (addOsanoEventListener(cm, eventName, handler)) {
          debugLog('Registered Osano.cm listener for ' + eventName);
        }

        /*
          Osano CMP commonly dispatches through Osano.cm.addEventListener.
          The document listener is intentionally harmless fallback coverage for
          integrations that proxy Osano CMP events as DOM CustomEvents.
        */
        if (addDocumentEventListener(eventName, handler)) {
          debugLog('Registered document listener for ' + eventName);
        }
      })(CONSENT_EVENTS[i]);
    }
  }

  function getCustomerPrivacySnapshot(customerPrivacy) {
    function call(methodName) {
      try {
        return typeof customerPrivacy[methodName] === 'function' ? customerPrivacy[methodName]() : undefined;
      } catch (error) {
        return 'Error: ' + error.message;
      }
    }

    return {
      currentVisitorConsent: call('currentVisitorConsent'),
      analyticsProcessingAllowed: call('analyticsProcessingAllowed'),
      marketingAllowed: call('marketingAllowed'),
      shouldShowBanner: call('shouldShowBanner'),
      getRegion: call('getRegion')
    };
  }

  window.syncOsanoConsentToShopifyDebug = function() {
    var rawOsanoConsent = getOsanoConsent();
    var mappedShopifyConsent = rawOsanoConsent ? mapOsanoConsentToShopify(rawOsanoConsent) : null;

    return loadShopifyConsentApi()
      .then(function(customerPrivacy) {
        var report = {
          rawOsanoConsent: rawOsanoConsent,
          mappedShopifyConsent: mappedShopifyConsent,
          shopifyCustomerPrivacy: getCustomerPrivacySnapshot(customerPrivacy)
        };

        if (window.console && window.console.log) {
          window.console.log(LOG_PREFIX, report);
        }

        return report;
      })
      .catch(function(error) {
        var report = {
          rawOsanoConsent: rawOsanoConsent,
          mappedShopifyConsent: mappedShopifyConsent,
          error: error && error.message ? error.message : error
        };

        if (window.console && window.console.warn) {
          window.console.warn(LOG_PREFIX, report);
        }

        return report;
      });
  };

  function start() {
    if (hasStarted) return;
    hasStarted = true;

    Promise.all([loadShopifyConsentApi(), waitForOsanoCm()])
      .then(function(results) {
        var cm = results[1];

        registerOsanoListeners(cm);
        return syncCurrentOsanoConsent('initial-current-consent');
      })
      .catch(function(error) {
        debugWarn('Initialization skipped', error);
      });
  }

  start();
})();
